/**
 * Deep link handler for Capacitor native apps.
 * Routes incoming URLs (universal links, custom scheme, push taps) to React Router.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNative } from '../platform';
import { onDeepLink } from '../platform/app-lifecycle';

export function useDeepLinks(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;

    return onDeepLink((rawUrl) => {
      try {
        const url = new URL(rawUrl);

        // Magic link: contains access_token in hash or query
        // e.g. mailander://auth#access_token=xxx&refresh_token=xxx
        //   or https://app.mailander.com/#access_token=xxx
        const hash = url.hash?.startsWith('#') ? url.hash.slice(1) : '';
        const hashParams = new URLSearchParams(hash);
        const queryParams = url.searchParams;

        if (hashParams.get('access_token') || queryParams.get('access_token')) {
          // Reconstruct the hash/query for the auth context to pick up
          navigate(`/${url.hash || url.search}`, { replace: true });
          return;
        }

        // Password reset: /login/reset-password?code=xxx
        if (url.pathname.includes('reset-password')) {
          const code = queryParams.get('code') || queryParams.get('token') || '';
          navigate(`/login/reset-password${code ? `?code=${code}` : ''}`, { replace: true });
          return;
        }

        // Chat deep link: /chat/conversation-id
        if (url.pathname.startsWith('/chat/')) {
          navigate(url.pathname, { replace: true });
          return;
        }

        // Notification deep link
        if (url.pathname === '/notifications' || url.pathname.startsWith('/notifications')) {
          navigate('/notifications', { replace: true });
          return;
        }

        // OAuth callback: /auth/callback?code=xxx
        if (url.pathname.includes('/auth/callback') || url.pathname.includes('/oauth')) {
          navigate(`${url.pathname}${url.search}`, { replace: true });
          return;
        }

        // Fallback: navigate to the path
        const path = url.pathname || '/';
        navigate(path + url.search, { replace: true });
      } catch {
        // Invalid URL — ignore
      }
    });
  }, [navigate]);
}
